// ============================================================
//                      services/Api.js  (FINAL FINAL)
// ============================================================

import axios from 'axios';
import oauth from 'axios-oauth-client';
import tokenProvider from 'axios-token-interceptor';
import pRetry from 'p-retry';
import {application} from 'configs/app';
import store from 'store';
import {queryCache} from 'react-query';

function getMaxAge(res) {
  return res.expires_in * 1000;
}

export class ApiError extends Error {
  constructor(data, e, ...params) {
    super(...params);

    this.name = 'ApiError';
    this.data = data;
    this.e = e;

    this.message =
      params[0] ||
      data?.message ||
      data?.msg ||
      e?.message ||
      'خطایی رخ داده است';

    this.code = data?.error || data?.name || 'UNKNOWN';
  }
}

class Api {
  _cachedToken = null;
  _hardLogout = false;
  _refreshInProgress = false;

  constructor() {
    this.instance = axios.create({
      baseURL: application.endpoint,
      timeout: 15000,
      headers: {
        'Accept-Language': 'fa-IR, en;q=0.8',
        'Cache-Control': 'no-store',
        Pragma: 'no-cache',
      },
    });

    this._injectTokenMiddleware();
  }

  // ---------------------------------------------------------
  // TOKEN INJECTION (Safe & Same as original behavior)
  // ---------------------------------------------------------
  _injectTokenMiddleware() {
    this.instance.interceptors.request.use(async (config) => {
      if (this._hardLogout) return config; // ← NO TOKEN AFTER LOGOUT

      if (!this._cachedToken) {
        const refresh = localStorage.getItem('refreshToken');
        if (!refresh) return config;

        this._cachedToken = tokenProvider.tokenCache(this.refreshToken, {
          getMaxAge,
        });
      }

      const token = await this._cachedToken().catch(() => null);

      if (token?.access_token) {
        config.headers['Authorization'] = 'Bearer ' + token.access_token;
      }

      return config;
    });
  }

  // ---------------------------------------------------------
  // PASSWORD LOGIN
  // ---------------------------------------------------------
  passwordGrant = (username, password) => {
    return pRetry(
      async () => {
        const getToken = oauth.client(axios.create({timeout: 15000}), {
          url: application.endpoint + application.tokenPath,
          grant_type: 'password',
          client_id: application.id,
          username,
          password,
        });

        const token = await getToken();

        localStorage.setItem('refreshToken', token.refresh_token);
        this._hardLogout = false;

        this._cachedToken = tokenProvider.tokenCache(
          () => Promise.resolve(token),
          {getMaxAge},
        );

        store.getActions().auth.login();
        return token;
      },
      {retries: 0},
    );
  };

  // ---------------------------------------------------------
  // REFRESH TOKEN (Blocked in logout mode)
  // ---------------------------------------------------------
  refreshToken = async () => {
    if (this._hardLogout) return null; // ← CRITICAL: DO NOT REFRESH AFTER LOGOUT
    if (this._refreshInProgress) return null;

    this._refreshInProgress = true;

    try {
      const old = localStorage.getItem('refreshToken');
      if (!old) throw new Error('No refresh token');

      const getToken = oauth.client(axios.create({timeout: 12000}), {
        url: application.endpoint + application.tokenPath,
        grant_type: 'refresh_token',
        client_id: application.id,
        refresh_token: old,
      });

      const res = await getToken();

      localStorage.setItem('refreshToken', res.refresh_token || old);

      return res;
    } finally {
      this._refreshInProgress = false;
    }
  };

  // ---------------------------------------------------------
  // GET CACHED TOKEN
  // ---------------------------------------------------------
  getCachedToken = async () => {
    if (this._hardLogout) return null;

    if (!this._cachedToken) {
      const refresh = localStorage.getItem('refreshToken');
      if (!refresh) return null;

      this._cachedToken = tokenProvider.tokenCache(this.refreshToken, {
        getMaxAge,
      });
    }

    try {
      return await this._cachedToken();
    } catch {
      return null;
    }
  };

  // ---------------------------------------------------------
  // LOGOUT (Real + Persistent + Safe)
  // ---------------------------------------------------------
  logout = () => {
    try {
      this._hardLogout = true;

      localStorage.removeItem('refreshToken');
      this._cachedToken = null;

      queryCache.clear();

      try {
        window.dispatchEvent(new Event('logout'));
      } catch {}

      store.getActions().auth.logout();
    } catch {}
  };

  // ---------------------------------------------------------
  // REQUEST WRAPPER
  // ---------------------------------------------------------
  async _request(method, path, data, options) {
    try {
      const res = await this.instance.request({
        method,
        url: path,
        data,
        params: options?.params,
        headers: options?.headers,
        timeout: options?.timeout,
      });

      return res;
    } catch (err) {
      if (!err.response) {
        throw new ApiError(null, err, 'ارتباط با سرور برقرار نشد');
      }

      // ---------------------
      // HANDLE 401
      // ---------------------
      if (err.response.status === 401 && !this._hardLogout) {
        const newToken = await this.refreshToken().catch(() => null);

        if (newToken) {
          this._cachedToken = tokenProvider.tokenCache(
            () => Promise.resolve(newToken),
            {getMaxAge},
          );

          return this._request(method, path, data, options);
        }

        this.logout();
        throw new ApiError(null, err, 'دوباره وارد شوید');
      }

      throw new ApiError(err.response.data, err);
    }
  }

  invoke = (method, path, data, options = {}) => {
    return pRetry(() => this._request(method, path, data, options), {
      retries: method === 'GET' ? 1 : 0,
    });
  };
}

const api = new Api();
api.Error = ApiError;

export default api;
